import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { api } from '../lib/api';

type Reg = { id: number; serialNumber: string; product: { name: string } | null };

export default function NewClaim() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [regs, setRegs]       = useState<Reg[]>([]);
  const [form, setForm]       = useState({ registrationId: '', subject: '', description: '', priority: 'normal' });
  const [error, setError]     = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get<Reg[]>('/registrations').then(r => setRegs(r.filter(x => x.product))); }, []);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(''); setSubmitting(true);
    try {
      const claim = await api.post<{ id: number }>('/claims', { ...form, registrationId: Number(form.registrationId) });
      navigate(`/claims/${claim.id}`);
    } catch (err: any) { setError(err.message); setSubmitting(false); }
  }

  return (
    <Layout>
      <div className="max-w-lg mx-auto px-4 sm:px-0">
        <h1 className="text-2xl font-bold mb-6">{t('claims.newTitle')}</h1>
        {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-4">{error}</p>}
        <form onSubmit={handleSubmit} className="card space-y-4">
          <div>
            <label className="label">{t('claims.selectReg')}</label>
            <select className="input" required value={form.registrationId} onChange={set('registrationId')}>
              <option value="">{t('claims.selectRegPlaceholder')}</option>
              {regs.map(r => <option key={r.id} value={r.id}>{r.product?.name} — {r.serialNumber}</option>)}
            </select>
          </div>
          <div>
            <label className="label">{t('claims.subjectLabel')}</label>
            <input className="input" required value={form.subject} onChange={set('subject')} placeholder={t('claims.subjectPlaceholder')} />
          </div>
          <div>
            <label className="label">{t('claims.priorityLabel')}</label>
            <select className="input" value={form.priority} onChange={set('priority')}>
              <option value="low">{t('claims.priorityLow')}</option>
              <option value="normal">{t('claims.priorityMedium')}</option>
              <option value="high">{t('claims.priorityHigh')}</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div>
            <label className="label">{t('claims.descLabel')}</label>
            <textarea className="input" required rows={5} value={form.description} onChange={set('description')}
              placeholder={t('claims.descPlaceholder')} />
          </div>
          <button className="btn-primary w-full sm:w-auto" type="submit" disabled={submitting}>
            {submitting ? t('claims.submitting') : t('claims.submit')}
          </button>
        </form>
      </div>
    </Layout>
  );
}
